import { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { GripVertical, Search } from 'lucide-react';
import { listBlocksByCategory } from '@sytel/blocks';
import type { BlockDefinition } from '@sytel/blocks';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

const CATEGORY_LABELS: Record<string, string> = {
  hero: 'Hero',
  content: 'Contenido',
  media: 'Media',
  'social-proof': 'Social proof',
  data: 'Datos',
  cta: 'CTAs',
  collection: 'Colecciones',
  form: 'Formularios',
  layout: 'Layout',
};

interface BlockPaletteProps {
  onAdd: (definition: BlockDefinition) => void;
}

export function BlockPalette({ onAdd }: BlockPaletteProps) {
  const byCat = listBlocksByCategory();
  const [q, setQ] = useState('');

  const filtered = Object.entries(byCat).reduce((acc, [cat, blocks]) => {
    const matches = blocks.filter((b) => {
      if (!q) return true;
      const t = q.toLowerCase();
      return (
        b.meta.label.toLowerCase().includes(t) ||
        (b.meta.description ?? '').toLowerCase().includes(t) ||
        b.meta.type.toLowerCase().includes(t)
      );
    });
    if (matches.length > 0) acc[cat] = matches;
    return acc;
  }, {} as Record<string, BlockDefinition[]>);

  return (
    <aside className="w-72 shrink-0 overflow-y-auto border-r border-neutral-200 bg-white">
      <div className="sticky top-0 z-10 border-b border-neutral-200 bg-white p-3">
        <h2 className="mb-2 text-sm font-semibold">Bloques</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-neutral-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar bloque…"
            className="h-8 pl-8 text-xs"
          />
        </div>
        <p className="mt-2 text-[10px] text-neutral-400">
          Arrastra al canvas o haz click para añadir al final.
        </p>
      </div>
      <div className="space-y-5 p-3">
        {Object.entries(filtered).map(([cat, blocks]) => (
          <div key={cat}>
            <h3 className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
              {CATEGORY_LABELS[cat] ?? cat}
            </h3>
            <ul className="space-y-1">
              {blocks.map((b) => (
                <DraggablePaletteItem key={b.meta.type} definition={b} onClick={() => onAdd(b)} />
              ))}
            </ul>
          </div>
        ))}
        {Object.keys(filtered).length === 0 && (
          <p className="px-1 py-6 text-center text-xs text-neutral-400">
            Ningún bloque coincide con "{q}"
          </p>
        )}
      </div>
    </aside>
  );
}

function DraggablePaletteItem({
  definition, onClick,
}: {
  definition: BlockDefinition;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${definition.meta.type}`,
    data: {
      source: 'palette',
      blockType: definition.meta.type,
    },
  });

  return (
    <li>
      <div
        ref={setNodeRef}
        {...attributes}
        className={cn(
          'group flex items-stretch gap-0 overflow-hidden rounded-md border border-neutral-200 bg-white transition-shadow',
          isDragging ? 'opacity-30' : 'hover:border-neutral-400 hover:shadow-sm',
        )}
      >
        <button
          {...listeners}
          type="button"
          className="flex shrink-0 cursor-grab items-center justify-center border-r border-neutral-100 px-1.5 text-neutral-300 hover:bg-neutral-50 hover:text-neutral-600 active:cursor-grabbing"
          aria-label="Arrastrar bloque"
        >
          <GripVertical className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={onClick}
          className="flex-1 px-2 py-1.5 text-left"
        >
          <div className="text-xs font-medium leading-tight">{definition.meta.label}</div>
          <div className="line-clamp-2 text-[10px] leading-snug text-neutral-500">
            {definition.meta.description}
          </div>
        </button>
      </div>
    </li>
  );
}
